import { useState, useEffect, useCallback, createContext, useContext } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("mhlontlo_token"));
  useEffect(() => {
    if (token) {
      fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject()).then(setUser)
        .catch(() => { setToken(null); localStorage.removeItem("mhlontlo_token"); });
    }
  }, [token]);
  const login = async (username, password) => {
    const r = await fetch(`${API}/auth/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
    if (!r.ok) throw new Error("Invalid credentials");
    const data = await r.json();
    localStorage.setItem("mhlontlo_token", data.token); setToken(data.token); setUser(data.user);
  };
  const logout = () => { localStorage.removeItem("mhlontlo_token"); setToken(null); setUser(null); };
  return <AuthContext.Provider value={{ user, token, login, logout }}>{children}</AuthContext.Provider>;
}
function useAuth() { return useContext(AuthContext); }

function useRouter() {
  const [hash, setHash] = useState(window.location.hash || "#/");
  useEffect(() => { const h = () => setHash(window.location.hash || "#/"); window.addEventListener("hashchange", h); return () => window.removeEventListener("hashchange", h); }, []);
  return { path: hash.slice(1), navigate: p => { window.location.hash = p; window.scrollTo(0, 0); } };
}

function apiFetch(path, token, opts = {}) {
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  return fetch(`${API}${path}`, { ...opts, headers });
}

function fileToBase64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

// ── Shared UI ──
const gold = "#c8a45a"; const dark = "#0a1628"; const green = "#7a9a7a"; const dimGreen = "#506838";

function Navbar({ navigate, path }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const links = [{ to: "/", label: "Home" }, { to: "/about", label: "About" }, { to: "/events", label: "Events" }, { to: "/contact", label: "Contact" }];
  const active = to => path === to || (to !== "/" && path.startsWith(to));
  const navBtn = (to, label, extra = {}) => (
    <button key={to} onClick={() => { navigate(to); setOpen(false); }} style={{ background: active(to) ? "rgba(200,164,90,0.15)" : "transparent", color: active(to) ? gold : "#b0b8c0", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 500, ...extra }}>{label}</button>
  );
  return (
    <nav style={{ background: `linear-gradient(135deg, ${dark} 0%, #162a1a 100%)`, borderBottom: `3px solid ${gold}`, position: "sticky", top: 0, zIndex: 1000, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 64, flexWrap: "wrap" }}>
        <div onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: `linear-gradient(135deg, ${gold}, #8b6914)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🏇</div>
          <div><div style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>KUMKANI MHLONTLO</div><div style={{ color: green, fontSize: 9, letterSpacing: 3, textTransform: "uppercase" }}>Legacy Festival</div></div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          {links.map(l => navBtn(l.to, l.label))}
          {user ? (<><button onClick={() => { navigate("/admin"); setOpen(false); }} style={{ background: "rgba(200,164,90,0.2)", color: gold, border: `1px solid rgba(200,164,90,0.3)`, padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Dashboard</button><button onClick={() => { logout(); navigate("/"); }} style={{ background: "transparent", color: "#e07070", border: "none", padding: "7px 10px", cursor: "pointer", fontSize: 13 }}>Logout</button></>) : (
            <button onClick={() => navigate("/login")} style={{ background: `linear-gradient(135deg, ${gold}, #a07820)`, color: dark, border: "none", padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Admin Login</button>
          )}
        </div>
      </div>
    </nav>
  );
}

function Footer({ navigate }) {
  return (
    <footer style={{ background: `linear-gradient(135deg, ${dark} 0%, #0d1f10 100%)`, borderTop: `3px solid ${gold}`, padding: "40px 20px 20px", color: "#7a8a7a" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 28 }}>
        <div>
          <div style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Kumkani Mhlontlo</div>
          <div style={{ fontSize: 13, lineHeight: 1.8 }}>Legacy Festival — Celebrating equestrian heritage &amp; community empowerment. OR Tambo District, Eastern Cape.</div>
        </div>
        <div>
          <div style={{ color: gold, fontSize: 13, fontWeight: 600, marginBottom: 10, letterSpacing: 1 }}>LINKS</div>
          {["/about", "/events", "/contact", "/privacy", "/terms"].map(l => (<div key={l} onClick={() => navigate(l)} style={{ cursor: "pointer", fontSize: 13, lineHeight: 2.2 }}>{l.slice(1).replace(/^\w/, c => c.toUpperCase()).replace("-", " & ")}</div>))}
        </div>
        <div>
          <div style={{ color: gold, fontSize: 13, fontWeight: 600, marginBottom: 10, letterSpacing: 1 }}>CONTACT</div>
          <div style={{ fontSize: 13, lineHeight: 2.2 }}>📍 Qumbu, Eastern Cape<br />📞 047 553 0040<br />✉️ info@mhlontlolm.gov.za</div>
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: "28px auto 0", paddingTop: 16, borderTop: "1px solid rgba(200,164,90,0.15)", textAlign: "center", fontSize: 11, color: "#506050" }}>
        © {new Date().getFullYear()} Kumkani Mhlontlo Local Municipality. All rights reserved. | <span style={{ cursor: "pointer" }} onClick={() => navigate("/privacy")}>Privacy</span> | <span style={{ cursor: "pointer" }} onClick={() => navigate("/terms")}>Terms</span>
      </div>
    </footer>
  );
}

function PageShell({ title, subtitle, children }) {
  return (<div style={{ minHeight: "60vh" }}>
    <div style={{ background: "linear-gradient(135deg, #0e2218 0%, #1a3520 50%, #0a1628 100%)", padding: "50px 20px", textAlign: "center", borderBottom: "2px solid rgba(200,164,90,0.2)" }}>
      <h1 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: "clamp(26px, 5vw, 40px)", margin: 0, fontWeight: 700 }}>{title}</h1>
      {subtitle && <p style={{ color: green, marginTop: 8, fontSize: "clamp(13px, 2vw, 15px)", maxWidth: 600, margin: "8px auto 0" }}>{subtitle}</p>}
    </div>
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 20px" }}>{children}</div>
  </div>);
}

function Card({ children, style, onClick, hover }) {
  const [h, setH] = useState(false);
  return (<div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
    style={{ background: "linear-gradient(135deg, #111d14 0%, #0d1a24 100%)", border: "1px solid rgba(200,164,90,0.15)", borderRadius: 12, overflow: "hidden", transition: "all 0.3s", transform: hover && h ? "translateY(-3px)" : "none", boxShadow: hover && h ? "0 12px 36px rgba(200,164,90,0.12)" : "0 4px 16px rgba(0,0,0,0.2)", cursor: onClick ? "pointer" : "default", ...style }}>{children}</div>);
}

function Btn({ children, onClick, variant = "primary", style, disabled, type }) {
  const s = { primary: { background: `linear-gradient(135deg, ${gold}, #a07820)`, color: dark, fontWeight: 600 }, secondary: { background: "rgba(200,164,90,0.1)", color: gold, border: "1px solid rgba(200,164,90,0.3)" }, danger: { background: "rgba(220,60,60,0.15)", color: "#e07070", border: "1px solid rgba(220,60,60,0.3)" }, success: { background: "rgba(60,180,80,0.15)", color: "#70e080", border: "1px solid rgba(60,180,80,0.3)" } };
  return <button type={type} onClick={onClick} disabled={disabled} style={{ ...(s[variant] || s.primary), border: s[variant]?.border || "none", padding: "9px 22px", borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer", fontSize: 14, transition: "all 0.2s", opacity: disabled ? 0.5 : 1, ...style }}>{children}</button>;
}

function Img({ base64, mime, alt, style }) {
  if (!base64) return <div style={{ background: "#1a2a1a", display: "flex", alignItems: "center", justifyContent: "center", color: "#506050", fontSize: 48, ...style }}>🐴</div>;
  return <img src={`data:${mime || "image/jpeg"};base64,${base64}`} alt={alt || ""} style={{ objectFit: "cover", ...style }} />;
}

function Loading() { return <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><div style={{ width: 36, height: 36, border: "3px solid rgba(200,164,90,0.2)", borderTop: `3px solid ${gold}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} /></div>; }

function Input({ label, value, onChange, type = "text", required, textarea, placeholder }) {
  const s = { width: "100%", padding: "9px 12px", background: dark, border: "1px solid rgba(200,164,90,0.2)", borderRadius: 8, color: "#d0d8d0", fontSize: 14, outline: "none", boxSizing: "border-box" };
  return (<div style={{ marginBottom: 14 }}>{label && <label style={{ display: "block", color: "#8a9a8a", fontSize: 13, marginBottom: 5 }}>{label}{required && <span style={{ color: gold }}> *</span>}</label>}
    {textarea ? <textarea value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} rows={3} style={{ ...s, resize: "vertical" }} /> : <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} style={s} />}</div>);
}

function ImageUpload({ onUpload, label = "Upload Image" }) {
  const ref = useCallback(node => { if (node) node.value = ""; }, []);
  const handle = async e => { const f = e.target.files[0]; if (f) { const b = await fileToBase64(f); onUpload(b); } };
  return (<div style={{ marginBottom: 14 }}><label style={{ display: "block", color: "#8a9a8a", fontSize: 13, marginBottom: 5 }}>{label}</label>
    <input ref={ref} type="file" accept="image/*" onChange={handle} style={{ color: "#8a9a8a", fontSize: 13 }} /></div>);
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{ background: "linear-gradient(135deg, #111d14, #0d1a24)", border: `1px solid rgba(200,164,90,0.2)`, borderRadius: 12, maxWidth: 600, width: "100%", maxHeight: "85vh", overflow: "auto", padding: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", margin: 0, fontSize: 20 }}>{title}</h3>
        <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#e07070", fontSize: 20, cursor: "pointer" }}>✕</button>
      </div>
      {children}
    </div>
  </div>);
}

// ── Pages ──

function HomePage({ navigate }) {
  const [years, setYears] = useState([]);
  useEffect(() => { fetch(`${API}/years`).then(r => r.json()).then(setYears); }, []);
  return (<div>
    <div style={{ background: "linear-gradient(160deg, #0a1628 0%, #162a1a 40%, #1a3520 70%, #0e2218 100%)", minHeight: "80vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "60px 20px", position: "relative" }}>
      <div style={{ position: "absolute", top: 60, left: "10%", width: 200, height: 200, background: "radial-gradient(circle, rgba(200,164,90,0.06), transparent 70%)", borderRadius: "50%" }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 64, marginBottom: 12 }}>🏇</div>
        <div style={{ color: dimGreen, fontSize: 12, letterSpacing: 5, textTransform: "uppercase", marginBottom: 14 }}>Kumkani Mhlontlo Local Municipality Presents</div>
        <h1 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: "clamp(34px, 7vw, 68px)", margin: "0 0 6px", fontWeight: 700, lineHeight: 1.1 }}>Legacy Festival</h1>
        <h2 style={{ color: green, fontFamily: "'Playfair Display',Georgia,serif", fontSize: "clamp(16px, 3.5vw, 28px)", margin: "0 0 20px", fontWeight: 400, fontStyle: "italic" }}>Annual Horse Racing Championship</h2>
        <p style={{ color: "#8a9a8a", maxWidth: 560, margin: "0 auto 32px", fontSize: "clamp(13px, 2vw, 16px)", lineHeight: 1.8 }}>Celebrating equestrian heritage, community empowerment and the cultural traditions of the AmaMpondomise Kingdom — every 26th of December in Qumbu, Eastern Cape.</p>
        <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
          <Btn onClick={() => navigate("/events")} style={{ padding: "12px 32px", fontSize: 15 }}>Explore Events</Btn>
          <Btn variant="secondary" onClick={() => navigate("/about")} style={{ padding: "12px 32px", fontSize: 15 }}>Learn More</Btn>
        </div>
      </div>
      <div style={{ position: "relative", zIndex: 1, marginTop: 60, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 20, maxWidth: 600, width: "100%" }}>
        {[{ n: "26 Dec", l: "Every Year" }, { n: years.length + "+", l: "Editions" }, { n: "Qumbu", l: "Eastern Cape" }, { n: "Free", l: "Community" }].map((s, i) => (
          <div key={i} style={{ textAlign: "center" }}><div style={{ color: gold, fontSize: 22, fontWeight: 700, fontFamily: "'Playfair Display',Georgia,serif" }}>{s.n}</div><div style={{ color: dimGreen, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginTop: 3 }}>{s.l}</div></div>
        ))}
      </div>
    </div>
    <div style={{ background: "#080e14", padding: "60px 20px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}><div style={{ color: dimGreen, fontSize: 11, letterSpacing: 4, textTransform: "uppercase", marginBottom: 6 }}>Explore</div><h2 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 28, margin: 0 }}>Festival Editions</h2></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 18 }}>
          {years.map(y => (<Card key={y.id} hover onClick={() => navigate(`/events/${y.id}`)}>
            <div style={{ padding: 20, textAlign: "center" }}><div style={{ fontSize: 40, marginBottom: 6 }}>🏆</div><div style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, fontWeight: 700 }}>{y.year}</div><div style={{ color: green, fontSize: 12, marginTop: 3 }}>26 Dec • {y.horse_count} horses • {y.heat_count} heats</div></div>
          </Card>))}
        </div>
      </div>
    </div>
  </div>);
}

function AboutPage() {
  return (<PageShell title="About the Festival" subtitle="Celebrating equestrian heritage & the AmaMpondomise Kingdom">
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <Card style={{ padding: 28, marginBottom: 20 }}>
        <h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, marginTop: 0 }}>The Kumkani Mhlontlo Legacy Festival</h3>
        <p style={{ color: "#a0b0a0", lineHeight: 1.9, fontSize: 15 }}>The Kumkani Mhlontlo Legacy Festival is an annual celebration held every 26th of December in Qumbu, within the Kumkani Mhlontlo Local Municipality of the Eastern Cape. The festival prominently features horse racing as its central attraction, honouring the rich equestrian culture and heritage of the AmaMpondomise people.</p>
        <p style={{ color: "#a0b0a0", lineHeight: 1.9, fontSize: 15 }}>Named after King Mhlontlo, a revered 19th-century leader of the AmaMpondomise, this festival celebrates the deep connection between the community and their horses — a tradition passed down through generations. The municipality, situated along the N2 between Mthatha and KwaBhaca, encompasses Qumbu and Tsolo and surrounding villages.</p>
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>
        {[{ i: "🏇", t: "Horse Racing", d: "Thrilling races featuring local horses and jockeys across sprints, endurance classics, and derby events." }, { i: "🤝", t: "Community Empowerment", d: "Opportunities for local breeders, trainers and jockeys to showcase skills and grow their businesses." }, { i: "🎭", t: "Cultural Heritage", d: "Celebrating AmaMpondomise traditions through dance, music, and ceremonial displays." }, { i: "📈", t: "Economic Growth", d: "Promoting tourism, enterprise and development in the OR Tambo District." }].map((c, idx) => (
          <Card key={idx} style={{ padding: 20, textAlign: "center" }}><div style={{ fontSize: 32, marginBottom: 8 }}>{c.i}</div><h4 style={{ color: gold, margin: "0 0 6px", fontFamily: "'Playfair Display',Georgia,serif" }}>{c.t}</h4><p style={{ color: "#8a9a8a", fontSize: 13, lineHeight: 1.7, margin: 0 }}>{c.d}</p></Card>
        ))}
      </div>
    </div>
  </PageShell>);
}

function ContactPage() {
  const [sent, setSent] = useState(false);
  return (<PageShell title="Contact Us" subtitle="Get in touch with the festival organisers">
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 28, maxWidth: 860, margin: "0 auto" }}>
      <Card style={{ padding: 28 }}>
        <h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", marginTop: 0, fontSize: 18 }}>Send a Message</h3>
        {sent ? <div style={{ color: "#70e080", padding: 20, textAlign: "center" }}>✓ Thank you for your message!</div> : (<div><Input label="Full Name" value="" onChange={() => { }} required /><Input label="Email" value="" onChange={() => { }} type="email" required /><Input label="Message" value="" onChange={() => { }} textarea required /><Btn onClick={() => setSent(true)} style={{ width: "100%" }}>Send Message</Btn></div>)}
      </Card>
      <div>
        <Card style={{ padding: 20, marginBottom: 14 }}><h4 style={{ color: gold, marginTop: 0 }}>Qumbu Office</h4><div style={{ color: "#8a9a8a", fontSize: 13, lineHeight: 2 }}>📍 Qumbu, EC 5180<br />📞 047 553 0040<br />✉️ info@mhlontlolm.gov.za</div></Card>
        <Card style={{ padding: 20, marginBottom: 14 }}><h4 style={{ color: gold, marginTop: 0 }}>Tsolo Office</h4><div style={{ color: "#8a9a8a", fontSize: 13, lineHeight: 2 }}>📍 Tsolo, Eastern Cape<br />📞 047 542 0340</div></Card>
        <Card style={{ padding: 20 }}><h4 style={{ color: gold, marginTop: 0 }}>Ethics Hotline</h4><div style={{ color: "#e07070", fontSize: 16, fontWeight: 600 }}>0800 222 887</div><div style={{ color: "#8a9a8a", fontSize: 11 }}>24hrs, Anonymous</div></Card>
      </div>
    </div>
  </PageShell>);
}

function TextPage({ title, sections }) {
  return (<PageShell title={title}><div style={{ maxWidth: 800, margin: "0 auto" }}><Card style={{ padding: 28 }}>
    {sections.map((s, i) => (<div key={i} style={{ marginBottom: 20 }}><h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 17, margin: "0 0 6px" }}>{s.t}</h3><p style={{ color: "#8a9a8a", lineHeight: 1.8, fontSize: 14, margin: 0 }}>{s.p}</p></div>))}
    <div style={{ color: "#506050", fontSize: 11, marginTop: 20, paddingTop: 14, borderTop: "1px solid rgba(200,164,90,0.1)" }}>Last updated: December 2024</div>
  </Card></div></PageShell>);
}

const privacySections = [
  { t: "Information We Collect", p: "When you submit photos or contact us, we collect your name, email address, phone number, and uploaded images. For admin users, we collect login credentials." },
  { t: "How We Use Your Information", p: "Your information is used solely for managing the Legacy Festival — reviewing community photos, contacting you regarding the event, and improving our services." },
  { t: "Image Storage", p: "All images are stored securely within our database. Public submissions require administrator approval before display." },
  { t: "Data Protection", p: "We protect your information in accordance with the Protection of Personal Information Act (POPIA). Data is not shared with third parties without consent." },
  { t: "Your Rights", p: "You may request access to, correction of, or deletion of your personal information by contacting info@mhlontlolm.gov.za." },
  { t: "Contact", p: "For privacy enquiries: Kumkani Mhlontlo Local Municipality, 047 553 0040, info@mhlontlolm.gov.za." },
];
const termsSections = [
  { t: "Acceptance of Terms", p: "By using this website you agree to be bound by these terms." },
  { t: "Photo Submissions", p: "By submitting photos, you confirm ownership or permission, and grant a non-exclusive licence for display after admin approval. You may request removal at any time." },
  { t: "User Accounts", p: "Admin accounts are for authorised municipal staff only. Users must maintain credential confidentiality." },
  { t: "Content Accuracy", p: "While we strive for accuracy, the Municipality makes no warranties about the completeness or reliability of information." },
  { t: "Limitation of Liability", p: "The Municipality shall not be liable for losses arising from use of this website." },
  { t: "Governing Law", p: "These terms are governed by the laws of the Republic of South Africa." },
];

// ── Event Pages ──

function EventsPage({ navigate }) {
  const [years, setYears] = useState([]);
  useEffect(() => { fetch(`${API}/years`).then(r => r.json()).then(setYears); }, []);
  return (<PageShell title="Festival Events" subtitle="Select a year to explore horses, heats, and photos">
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 20 }}>
      {years.map(y => (<Card key={y.id} hover onClick={() => navigate(`/events/${y.id}`)}>
        <div style={{ padding: "28px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🏆</div>
          <div style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 32, fontWeight: 700 }}>{y.year}</div>
          <div style={{ color: green, fontSize: 13, marginTop: 4 }}>26 December • {y.venue}</div>
          <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(200,164,90,0.1)" }}>
            <div><span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>{y.horse_count}</span><div style={{ color: "#506050", fontSize: 11 }}>Horses</div></div>
            <div><span style={{ color: gold, fontWeight: 700, fontSize: 16 }}>{y.heat_count}</span><div style={{ color: "#506050", fontSize: 11 }}>Heats</div></div>
          </div>
        </div>
      </Card>))}
    </div>
  </PageShell>);
}

function EventYearPage({ yearId, navigate }) {
  const [year, setYear] = useState(null);
  useEffect(() => { fetch(`${API}/years/${yearId}`).then(r => r.json()).then(setYear); }, [yearId]);
  if (!year) return <Loading />;
  return (<PageShell title={`${year.year} Festival`} subtitle={`26 December ${year.year} — ${year.venue}`}>
    <Card style={{ padding: 20, marginBottom: 28 }}><p style={{ color: "#a0b0a0", lineHeight: 1.8, fontSize: 14, margin: 0 }}>{year.description}</p></Card>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
      {[{ i: "🐴", t: "Horses", d: `${year.horse_count} competing horses`, p: `/events/${yearId}/horses` },
      { i: "🏁", t: "Heats & Results", d: `${year.heat_count} race heats`, p: `/events/${yearId}/heats` },
      { i: "📸", t: "Photo Gallery", d: "Browse & share photos", p: `/events/${yearId}/gallery` }].map((c, i) => (
        <Card key={i} hover onClick={() => navigate(c.p)}><div style={{ padding: "36px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{c.i}</div>
          <h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, margin: "0 0 6px" }}>{c.t}</h3>
          <p style={{ color: "#8a9a8a", fontSize: 13, margin: 0 }}>{c.d}</p>
        </div></Card>
      ))}
    </div>
  </PageShell>);
}

function HorsesPage({ yearId, navigate }) {
  const [horses, setHorses] = useState([]); const [year, setYear] = useState(null); const [loading, setLoading] = useState(true);
  useEffect(() => { Promise.all([fetch(`${API}/years/${yearId}/horses`).then(r => r.json()), fetch(`${API}/years/${yearId}`).then(r => r.json())]).then(([h, y]) => { setHorses(h); setYear(y); setLoading(false); }); }, [yearId]);
  if (loading) return <Loading />;
  return (<PageShell title={`${year?.year} Horses`} subtitle={`All ${horses.length} horses competing`}>
    <Btn variant="secondary" onClick={() => navigate(`/events/${yearId}`)} style={{ marginBottom: 20 }}>← Back to {year?.year}</Btn>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 }}>
      {horses.map(h => (<Card key={h.id} hover onClick={() => navigate(`/horse/${h.id}`)}>
        <Img base64={h.image_base64} mime={h.image_mime} alt={h.name} style={{ width: "100%", height: 180, display: "block" }} />
        <div style={{ padding: 16 }}>
          <h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, margin: "0 0 3px" }}>{h.name}</h3>
          <div style={{ color: green, fontSize: 12, marginBottom: 10 }}>{h.breed} • {h.color}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, fontSize: 12 }}>
            <div style={{ color: "#506050" }}>Age: <span style={{ color: "#8a9a8a" }}>{h.age} yrs</span></div>
            <div style={{ color: "#506050" }}>From: <span style={{ color: "#8a9a8a" }}>{h.hometown}</span></div>
          </div>
          <div style={{ marginTop: 8, padding: "3px 8px", background: "rgba(200,164,90,0.1)", borderRadius: 4, display: "inline-block", color: gold, fontSize: 11 }}>{h.racing_category}</div>
        </div>
      </Card>))}
    </div>
  </PageShell>);
}

function HorseProfilePage({ horseId, navigate }) {
  const [horse, setHorse] = useState(null);
  useEffect(() => { fetch(`${API}/horses/${horseId}`).then(r => r.json()).then(setHorse); }, [horseId]);
  if (!horse) return <Loading />;
  return (<PageShell title={horse.name} subtitle={`${horse.breed} • ${horse.racing_category}`}>
    <Btn variant="secondary" onClick={() => navigate(`/events/${horse.event_year_id}/horses`)} style={{ marginBottom: 20 }}>← Back to Horses</Btn>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 28 }}>
      <Card><Img base64={horse.image_base64} mime={horse.image_mime} alt={horse.name} style={{ width: "100%", height: 360, display: "block" }} /></Card>
      <Card style={{ padding: 24 }}>
        <h2 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 26, margin: "0 0 16px" }}>{horse.name}</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
          {[["Age", `${horse.age} years`], ["Breed", horse.breed], ["Colour", horse.color], ["Hometown", horse.hometown], ["Owner", horse.owner_name], ["Jockey", horse.jockey_name]].map(([l, v], i) => (
            <div key={i}><div style={{ color: "#506050", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 3 }}>{l}</div><div style={{ color: "#d0d8d0", fontSize: 14 }}>{v || "—"}</div></div>
          ))}
        </div>
        <div style={{ padding: "6px 12px", background: "rgba(200,164,90,0.1)", borderRadius: 6, display: "inline-block", color: gold, fontSize: 13, marginBottom: 16 }}>{horse.racing_category}</div>
        <p style={{ color: "#a0b0a0", lineHeight: 1.8, fontSize: 14, margin: 0 }}>{horse.description}</p>
      </Card>
    </div>
  </PageShell>);
}

function HeatsPage({ yearId, navigate }) {
  const [heats, setHeats] = useState([]); const [year, setYear] = useState(null); const [loading, setLoading] = useState(true);
  useEffect(() => { Promise.all([fetch(`${API}/years/${yearId}/heats`).then(r => r.json()), fetch(`${API}/years/${yearId}`).then(r => r.json())]).then(([h, y]) => { setHeats(h); setYear(y); setLoading(false); }); }, [yearId]);
  if (loading) return <Loading />;
  const statusColor = s => s === "completed" ? "#70e080" : s === "cancelled" ? "#e07070" : gold;
  return (<PageShell title={`${year?.year} Heats & Results`} subtitle="All race heats and results">
    <Btn variant="secondary" onClick={() => navigate(`/events/${yearId}`)} style={{ marginBottom: 20 }}>← Back to {year?.year}</Btn>
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {heats.map(h => (<Card key={h.id} style={{ padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          <div>
            <h3 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 18, margin: 0 }}>Heat {h.heat_number}: {h.heat_name}</h3>
            <div style={{ color: green, fontSize: 12, marginTop: 3 }}>{h.category} • {h.distance} • {h.scheduled_time}</div>
          </div>
          <span style={{ padding: "4px 12px", borderRadius: 12, fontSize: 12, fontWeight: 600, color: statusColor(h.status), background: `${statusColor(h.status)}15`, border: `1px solid ${statusColor(h.status)}30`, textTransform: "capitalize" }}>{h.status}</span>
        </div>
        {h.entries.length > 0 && (
          <div style={{ borderTop: "1px solid rgba(200,164,90,0.1)", paddingTop: 12 }}>
            {h.entries.sort((a, b) => (a.position || 99) - (b.position || 99)).map(e => (
              <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid rgba(200,164,90,0.05)" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: e.is_winner ? "rgba(200,164,90,0.2)" : "rgba(100,100,100,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: e.is_winner ? gold : "#8a9a8a", fontWeight: 700, fontSize: 13 }}>{e.position || "—"}</div>
                <div style={{ flex: 1 }}>
                  <span style={{ color: e.is_winner ? gold : "#d0d8d0", fontWeight: e.is_winner ? 600 : 400, fontSize: 14, cursor: "pointer" }} onClick={() => navigate(`/horse/${e.horse_id}`)}>{e.horse_name}</span>
                  <span style={{ color: "#506050", fontSize: 12, marginLeft: 8 }}>{e.horse_hometown}</span>
                </div>
                {e.is_winner && <span style={{ fontSize: 16 }}>🏆</span>}
                {e.finish_time && <span style={{ color: "#506050", fontSize: 12 }}>{e.finish_time}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>))}
    </div>
  </PageShell>);
}

function GalleryPage({ yearId, navigate }) {
  const [eventPhotos, setEventPhotos] = useState([]); const [publicPhotos, setPublicPhotos] = useState([]);
  const [year, setYear] = useState(null); const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("event"); const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", title: "", description: "", image: null });
  const [submitting, setSubmitting] = useState(false); const [submitted, setSubmitted] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all([fetch(`${API}/years/${yearId}/photos`).then(r => r.json()), fetch(`${API}/years/${yearId}/gallery`).then(r => r.json()), fetch(`${API}/years/${yearId}`).then(r => r.json())])
      .then(([ep, pp, y]) => { setEventPhotos(ep); setPublicPhotos(pp); setYear(y); setLoading(false); });
  }, [yearId]);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !form.image) return alert("Please fill all required fields and upload an image.");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/years/${yearId}/submit-photo`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ submitter_name: form.name, submitter_email: form.email, submitter_phone: form.phone, title: form.title, description: form.description, image: form.image }) });
      if (r.ok) { setSubmitted(true); setForm({ name: "", email: "", phone: "", title: "", description: "", image: null }); }
    } catch (e) { alert("Error submitting photo"); }
    setSubmitting(false);
  };

  if (loading) return <Loading />;
  const photos = tab === "event" ? eventPhotos : publicPhotos;

  return (<PageShell title={`${year?.year} Photo Gallery`} subtitle="Browse and share photos from the festival">
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
      <Btn variant="secondary" onClick={() => navigate(`/events/${yearId}`)}>← Back to {year?.year}</Btn>
      <Btn onClick={() => setShowUpload(true)}>📷 Share Your Photos</Btn>
    </div>
    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
      {[["event", "Official Photos"], ["public", "Community Photos"]].map(([k, l]) => (
        <button key={k} onClick={() => setTab(k)} style={{ background: tab === k ? "rgba(200,164,90,0.15)" : "transparent", color: tab === k ? gold : "#8a9a8a", border: `1px solid ${tab === k ? "rgba(200,164,90,0.3)" : "transparent"}`, padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>{l}</button>
      ))}
    </div>
    {photos.length === 0 ? <Card style={{ padding: 40, textAlign: "center" }}><div style={{ color: "#506050", fontSize: 14 }}>No photos yet for this category.</div></Card> : (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
        {photos.map(p => (<Card key={p.id} hover onClick={() => setLightbox(p)} style={{ cursor: "pointer" }}>
          <Img base64={p.image_base64} mime={p.image_mime} alt={p.title} style={{ width: "100%", height: 180, display: "block" }} />
          <div style={{ padding: 10 }}>
            <div style={{ color: gold, fontSize: 13, fontWeight: 500 }}>{p.title || "Festival Photo"}</div>
            {tab === "public" && <div style={{ color: "#506050", fontSize: 11, marginTop: 2 }}>By {p.submitter_name}</div>}
          </div>
        </Card>))}
      </div>
    )}

    <Modal open={!!lightbox} onClose={() => setLightbox(null)} title={lightbox?.title || "Photo"}>
      {lightbox && <div><Img base64={lightbox.image_base64} mime={lightbox.image_mime} alt={lightbox.title} style={{ width: "100%", maxHeight: 500, display: "block", borderRadius: 8 }} />
        {lightbox.description && <p style={{ color: "#8a9a8a", marginTop: 12, fontSize: 14 }}>{lightbox.description}</p>}
        {lightbox.submitter_name && <div style={{ color: "#506050", fontSize: 12 }}>Shared by {lightbox.submitter_name}</div>}
      </div>}
    </Modal>

    <Modal open={showUpload} onClose={() => { setShowUpload(false); setSubmitted(false); }} title="Share Your Photos">
      {submitted ? <div style={{ textAlign: "center", padding: 20, color: "#70e080" }}><div style={{ fontSize: 36, marginBottom: 8 }}>✓</div><p>Photo submitted! It will appear once approved by an administrator.</p></div> : (
        <div><p style={{ color: "#8a9a8a", fontSize: 13, marginBottom: 16 }}>Share your memories from the festival. Your photo will be displayed after administrator approval.</p>
          <Input label="Full Name" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
          <Input label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} type="email" required />
          <Input label="Phone Number" value={form.phone} onChange={v => setForm({ ...form, phone: v })} required placeholder="e.g. 082 123 4567" />
          <Input label="Photo Title" value={form.title} onChange={v => setForm({ ...form, title: v })} placeholder="Optional" />
          <Input label="Description" value={form.description} onChange={v => setForm({ ...form, description: v })} textarea placeholder="Optional" />
          <ImageUpload label="Photo (required)" onUpload={b => setForm({ ...form, image: b })} />
          {form.image && <div style={{ marginBottom: 12 }}><img src={form.image} alt="preview" style={{ maxHeight: 100, borderRadius: 8, border: "1px solid rgba(200,164,90,0.2)" }} /></div>}
          <Btn onClick={handleSubmit} disabled={submitting} style={{ width: "100%" }}>{submitting ? "Submitting..." : "Submit Photo"}</Btn>
        </div>
      )}
    </Modal>
  </PageShell>);
}

// ── Login ──

function LoginPage({ navigate }) {
  const { login, user } = useAuth();
  const [username, setUsername] = useState(""); const [password, setPassword] = useState(""); const [error, setError] = useState("");
  useEffect(() => { if (user) navigate("/admin"); }, [user]);
  const handle = async e => { e.preventDefault(); setError(""); try { await login(username, password); navigate("/admin"); } catch { setError("Invalid credentials"); } };
  return (<PageShell title="Admin Login" subtitle="Municipality staff access only">
    <div style={{ maxWidth: 400, margin: "0 auto" }}><Card style={{ padding: 28 }}>
      <form onSubmit={handle}>
        {error && <div style={{ color: "#e07070", background: "rgba(220,60,60,0.1)", padding: "8px 12px", borderRadius: 6, fontSize: 13, marginBottom: 14 }}>{error}</div>}
        <Input label="Username" value={username} onChange={setUsername} required />
        <Input label="Password" value={password} onChange={setPassword} type="password" required />
        <Btn type="submit" style={{ width: "100%", marginTop: 8 }}>Sign In</Btn>
      </form>
      <div style={{ marginTop: 16, padding: 12, background: "rgba(200,164,90,0.05)", borderRadius: 6, fontSize: 12, color: "#506050" }}>Demo: admin / admin123</div>
    </Card></div>
  </PageShell>);
}

// ── Admin Dashboard ──

function AdminDashboard({ navigate }) {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null); const [tab, setTab] = useState("overview");
  const [years, setYears] = useState([]); const [selectedYear, setSelectedYear] = useState(null);
  const [horses, setHorses] = useState([]); const [heats, setHeats] = useState([]);
  const [subs, setSubs] = useState([]); const [subFilter, setSubFilter] = useState("pending");
  const [modal, setModal] = useState(null); const [form, setForm] = useState({});
  const [msg, setMsg] = useState("");

  useEffect(() => { if (!user) navigate("/login"); }, [user]);

  const load = useCallback(async () => {
    const r = await apiFetch("/admin/dashboard", token); if (r.ok) setStats(await r.json());
    const y = await fetch(`${API}/years`); setYears(await y.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedYear) {
      fetch(`${API}/years/${selectedYear}/horses`).then(r => r.json()).then(setHorses);
      fetch(`${API}/years/${selectedYear}/heats`).then(r => r.json()).then(setHeats);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (token) apiFetch(`/admin/submissions?status=${subFilter}`, token).then(r => r.json()).then(setSubs);
  }, [subFilter, token]);

  const showMsg = m => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  // Horse CRUD
  const saveHorse = async () => {
    const method = form.id ? "PUT" : "POST";
    const url = form.id ? `/admin/horses/${form.id}` : `/admin/years/${selectedYear}/horses`;
    const r = await apiFetch(url, token, { method, body: JSON.stringify(form) });
    if (r.ok) { showMsg("Horse saved!"); setModal(null); fetch(`${API}/years/${selectedYear}/horses`).then(r => r.json()).then(setHorses); load(); }
  };

  const deleteHorse = async id => {
    if (!confirm("Delete this horse?")) return;
    await apiFetch(`/admin/horses/${id}`, token, { method: "DELETE" });
    showMsg("Horse deleted"); fetch(`${API}/years/${selectedYear}/horses`).then(r => r.json()).then(setHorses); load();
  };

  // Heat CRUD
  const saveHeat = async () => {
    const method = form.id ? "PUT" : "POST";
    const url = form.id ? `/admin/heats/${form.id}` : `/admin/years/${selectedYear}/heats`;
    const r = await apiFetch(url, token, { method, body: JSON.stringify(form) });
    if (r.ok) { showMsg("Heat saved!"); setModal(null); fetch(`${API}/years/${selectedYear}/heats`).then(r => r.json()).then(setHeats); load(); }
  };

  const addEntry = async (heatId) => {
    const horseId = prompt("Enter Horse ID:");
    const pos = prompt("Position (leave blank if not yet raced):");
    if (!horseId) return;
    await apiFetch(`/admin/heats/${heatId}/entries`, token, { method: "POST", body: JSON.stringify({ horse_id: parseInt(horseId), position: pos ? parseInt(pos) : null, is_winner: pos === "1" }) });
    showMsg("Entry added!"); fetch(`${API}/years/${selectedYear}/heats`).then(r => r.json()).then(setHeats);
  };

  // Photo submission review
  const reviewSub = async (id, action) => {
    await apiFetch(`/admin/submissions/${id}/${action}`, token, { method: "POST" });
    showMsg(`Photo ${action}d!`); apiFetch(`/admin/submissions?status=${subFilter}`, token).then(r => r.json()).then(setSubs); load();
  };

  // Upload event photo
  const uploadPhoto = async () => {
    const r = await apiFetch(`/admin/years/${selectedYear}/photos`, token, { method: "POST", body: JSON.stringify(form) });
    if (r.ok) { showMsg("Photo uploaded!"); setModal(null); }
  };

  // Year CRUD
  const saveYear = async () => {
    const r = await apiFetch("/admin/years", token, { method: "POST", body: JSON.stringify(form) });
    if (r.ok) { showMsg("Year created!"); setModal(null); load(); }
  };

  if (!user) return null;

  const tabs = [
    { k: "overview", l: "Overview", i: "📊" },
    { k: "horses", l: "Horses", i: "🐴" },
    { k: "heats", l: "Heats", i: "🏁" },
    { k: "photos", l: "Photos", i: "📷" },
    { k: "submissions", l: "Submissions", i: "📨" },
  ];

  return (<div style={{ minHeight: "100vh", background: "#060c10" }}>
    <div style={{ background: "linear-gradient(135deg, #0e2218, #0a1628)", padding: "24px 20px", borderBottom: "2px solid rgba(200,164,90,0.15)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div><h1 style={{ color: gold, fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, margin: 0 }}>Admin Dashboard</h1><div style={{ color: green, fontSize: 13, marginTop: 2 }}>Welcome, {user.full_name}</div></div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={selectedYear || ""} onChange={e => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)} style={{ background: dark, color: "#d0d8d0", border: "1px solid rgba(200,164,90,0.2)", padding: "8px 12px", borderRadius: 6, fontSize: 13 }}>
            <option value="">Select Year</option>
            {years.map(y => <option key={y.id} value={y.id}>{y.year}</option>)}
          </select>
          <Btn variant="secondary" onClick={() => { setForm({ year: new Date().getFullYear() + 1, event_date: "", venue: "Qumbu Racecourse, Kumkani Mhlontlo Municipality", description: "" }); setModal("year"); }} style={{ fontSize: 12, padding: "8px 14px" }}>+ New Year</Btn>
        </div>
      </div>
    </div>

    {msg && <div style={{ background: "rgba(60,180,80,0.15)", color: "#70e080", padding: "10px 20px", textAlign: "center", fontSize: 14 }}>{msg}</div>}

    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 20px" }}>
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {tabs.map(t => (<button key={t.k} onClick={() => setTab(t.k)} style={{ background: tab === t.k ? "rgba(200,164,90,0.15)" : "transparent", color: tab === t.k ? gold : "#8a9a8a", border: `1px solid ${tab === t.k ? "rgba(200,164,90,0.3)" : "transparent"}`, padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>{t.i} {t.l}</button>))}
      </div>

      {/* Overview */}
      {tab === "overview" && stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
          {[["Festival Years", stats.total_years, "📅"], ["Total Horses", stats.total_horses, "🐴"], ["Total Heats", stats.total_heats, "🏁"], ["Pending Reviews", stats.pending_submissions, "📨"], ["Event Photos", stats.total_photos, "📷"], ["Approved Public", stats.approved_public_photos, "✅"]].map(([l, v, i], idx) => (
            <Card key={idx} style={{ padding: 20, textAlign: "center" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{i}</div>
              <div style={{ color: gold, fontSize: 28, fontWeight: 700, fontFamily: "'Playfair Display',Georgia,serif" }}>{v}</div>
              <div style={{ color: "#506050", fontSize: 12, marginTop: 4 }}>{l}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Horses */}
      {tab === "horses" && (<div>
        {!selectedYear ? <Card style={{ padding: 40, textAlign: "center" }}><div style={{ color: "#506050" }}>Please select a year above.</div></Card> : (<>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ color: "#8a9a8a", fontSize: 14 }}>{horses.length} horses</div>
            <Btn onClick={() => { setForm({ name: "", age: "", breed: "", color: "", hometown: "", owner_name: "", jockey_name: "", racing_category: "", description: "", image: null }); setModal("horse"); }} style={{ fontSize: 13 }}>+ Add Horse</Btn>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {horses.map(h => (<Card key={h.id} style={{ padding: 16 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <Img base64={h.image_base64} mime={h.image_mime} alt={h.name} style={{ width: 60, height: 60, borderRadius: 8, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: gold, fontWeight: 600, fontSize: 14 }}>{h.name}</div>
                  <div style={{ color: green, fontSize: 12 }}>{h.breed} • {h.hometown}</div>
                  <div style={{ color: "#506050", fontSize: 11 }}>ID: {h.id} • {h.racing_category}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                <Btn variant="secondary" onClick={() => { setForm(h); setModal("horse"); }} style={{ fontSize: 11, padding: "5px 10px" }}>Edit</Btn>
                <Btn variant="danger" onClick={() => deleteHorse(h.id)} style={{ fontSize: 11, padding: "5px 10px" }}>Delete</Btn>
              </div>
            </Card>))}
          </div>
        </>)}
      </div>)}

      {/* Heats */}
      {tab === "heats" && (<div>
        {!selectedYear ? <Card style={{ padding: 40, textAlign: "center" }}><div style={{ color: "#506050" }}>Please select a year above.</div></Card> : (<>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ color: "#8a9a8a", fontSize: 14 }}>{heats.length} heats</div>
            <Btn onClick={() => { setForm({ heat_number: heats.length + 1, heat_name: "", category: "", distance: "", scheduled_time: "", status: "upcoming" }); setModal("heat"); }} style={{ fontSize: 13 }}>+ Add Heat</Btn>
          </div>
          {heats.map(h => (<Card key={h.id} style={{ padding: 16, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ color: gold, fontWeight: 600, fontSize: 15 }}>Heat {h.heat_number}: {h.heat_name}</div>
                <div style={{ color: green, fontSize: 12 }}>{h.category} • {h.distance} • {h.scheduled_time} • <span style={{ textTransform: "capitalize" }}>{h.status}</span></div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <Btn variant="secondary" onClick={() => { setForm(h); setModal("heat"); }} style={{ fontSize: 11, padding: "5px 10px" }}>Edit</Btn>
                <Btn variant="secondary" onClick={() => addEntry(h.id)} style={{ fontSize: 11, padding: "5px 10px" }}>+ Entry</Btn>
              </div>
            </div>
            {h.entries.length > 0 && (<div style={{ marginTop: 10, borderTop: "1px solid rgba(200,164,90,0.1)", paddingTop: 8 }}>
              {h.entries.map(e => (<div key={e.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "4px 0", fontSize: 13, color: "#8a9a8a" }}>
                <span style={{ color: e.is_winner ? gold : "#506050", fontWeight: e.is_winner ? 600 : 400, width: 20 }}>{e.position || "—"}</span>
                <span style={{ color: e.is_winner ? gold : "#d0d8d0" }}>{e.horse_name}</span>
                {e.is_winner && <span>🏆</span>}
                <span style={{ color: "#506050", marginLeft: "auto" }}>{e.finish_time || ""}</span>
              </div>))}
            </div>)}
          </Card>))}
        </>)}
      </div>)}

      {/* Photos upload */}
      {tab === "photos" && (<div>
        {!selectedYear ? <Card style={{ padding: 40, textAlign: "center" }}><div style={{ color: "#506050" }}>Please select a year above.</div></Card> : (
          <Card style={{ padding: 24 }}>
            <h3 style={{ color: gold, marginTop: 0, fontSize: 18 }}>Upload Event Photo</h3>
            <Input label="Title" value={form.title || ""} onChange={v => setForm({ ...form, title: v })} />
            <Input label="Description" value={form.description || ""} onChange={v => setForm({ ...form, description: v })} textarea />
            <ImageUpload label="Photo" onUpload={b => setForm({ ...form, image: b })} />
            {form.image && <img src={form.image} alt="preview" style={{ maxHeight: 100, borderRadius: 8, marginBottom: 12 }} />}
            <Btn onClick={uploadPhoto}>Upload Photo</Btn>
          </Card>
        )}
      </div>)}

      {/* Submissions */}
      {tab === "submissions" && (<div>
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {["pending", "approved", "rejected"].map(s => (<button key={s} onClick={() => setSubFilter(s)} style={{ background: subFilter === s ? "rgba(200,164,90,0.15)" : "transparent", color: subFilter === s ? gold : "#8a9a8a", border: `1px solid ${subFilter === s ? "rgba(200,164,90,0.3)" : "transparent"}`, padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, textTransform: "capitalize" }}>{s}</button>))}
        </div>
        {subs.length === 0 ? <Card style={{ padding: 40, textAlign: "center" }}><div style={{ color: "#506050" }}>No {subFilter} submissions.</div></Card> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
            {subs.map(s => (<Card key={s.id} style={{ padding: 16 }}>
              <Img base64={s.image_base64} mime={s.image_mime} alt={s.title} style={{ width: "100%", height: 160, display: "block", borderRadius: 8, marginBottom: 12 }} />
              <div style={{ color: gold, fontWeight: 600, fontSize: 14 }}>{s.title || "Untitled"}</div>
              <div style={{ color: "#8a9a8a", fontSize: 12, marginTop: 4 }}>By: {s.submitter_name}</div>
              <div style={{ color: "#506050", fontSize: 12 }}>📧 {s.submitter_email} • 📞 {s.submitter_phone}</div>
              {s.description && <div style={{ color: "#506050", fontSize: 12, marginTop: 4 }}>{s.description}</div>}
              {subFilter === "pending" && (<div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <Btn variant="success" onClick={() => reviewSub(s.id, "approve")} style={{ fontSize: 12, padding: "6px 14px" }}>✓ Approve</Btn>
                <Btn variant="danger" onClick={() => reviewSub(s.id, "reject")} style={{ fontSize: 12, padding: "6px 14px" }}>✕ Reject</Btn>
              </div>)}
            </Card>))}
          </div>
        )}
      </div>)}
    </div>

    {/* Modals */}
    <Modal open={modal === "horse"} onClose={() => setModal(null)} title={form.id ? "Edit Horse" : "Add Horse"}>
      <Input label="Name" value={form.name || ""} onChange={v => setForm({ ...form, name: v })} required />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Age" value={form.age || ""} onChange={v => setForm({ ...form, age: v })} type="number" />
        <Input label="Breed" value={form.breed || ""} onChange={v => setForm({ ...form, breed: v })} />
        <Input label="Colour" value={form.color || ""} onChange={v => setForm({ ...form, color: v })} />
        <Input label="Hometown" value={form.hometown || ""} onChange={v => setForm({ ...form, hometown: v })} />
        <Input label="Owner" value={form.owner_name || ""} onChange={v => setForm({ ...form, owner_name: v })} />
        <Input label="Jockey" value={form.jockey_name || ""} onChange={v => setForm({ ...form, jockey_name: v })} />
      </div>
      <Input label="Racing Category" value={form.racing_category || ""} onChange={v => setForm({ ...form, racing_category: v })} />
      <Input label="Description" value={form.description || ""} onChange={v => setForm({ ...form, description: v })} textarea />
      <ImageUpload label="Horse Photo" onUpload={b => setForm({ ...form, image: b })} />
      {(form.image || form.image_base64) && <img src={form.image || `data:${form.image_mime};base64,${form.image_base64}`} alt="preview" style={{ maxHeight: 80, borderRadius: 8, marginBottom: 12 }} />}
      <Btn onClick={saveHorse} style={{ width: "100%" }}>{form.id ? "Update Horse" : "Add Horse"}</Btn>
    </Modal>

    <Modal open={modal === "heat"} onClose={() => setModal(null)} title={form.id ? "Edit Heat" : "Add Heat"}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Heat Number" value={form.heat_number || ""} onChange={v => setForm({ ...form, heat_number: parseInt(v) || "" })} type="number" required />
        <Input label="Heat Name" value={form.heat_name || ""} onChange={v => setForm({ ...form, heat_name: v })} required />
        <Input label="Category" value={form.category || ""} onChange={v => setForm({ ...form, category: v })} />
        <Input label="Distance" value={form.distance || ""} onChange={v => setForm({ ...form, distance: v })} />
        <Input label="Time" value={form.scheduled_time || ""} onChange={v => setForm({ ...form, scheduled_time: v })} />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", color: "#8a9a8a", fontSize: 13, marginBottom: 5 }}>Status</label>
        <select value={form.status || "upcoming"} onChange={e => setForm({ ...form, status: e.target.value })} style={{ width: "100%", padding: "9px 12px", background: dark, border: "1px solid rgba(200,164,90,0.2)", borderRadius: 8, color: "#d0d8d0", fontSize: 14 }}>
          <option value="upcoming">Upcoming</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
        </select>
      </div>
      <Btn onClick={saveHeat} style={{ width: "100%" }}>{form.id ? "Update Heat" : "Add Heat"}</Btn>
    </Modal>

    <Modal open={modal === "year"} onClose={() => setModal(null)} title="Create New Event Year">
      <Input label="Year" value={form.year || ""} onChange={v => setForm({ ...form, year: parseInt(v) || "" })} type="number" required />
      <Input label="Event Date" value={form.event_date || ""} onChange={v => setForm({ ...form, event_date: v })} type="date" required />
      <Input label="Venue" value={form.venue || ""} onChange={v => setForm({ ...form, venue: v })} />
      <Input label="Description" value={form.description || ""} onChange={v => setForm({ ...form, description: v })} textarea />
      <Btn onClick={saveYear} style={{ width: "100%" }}>Create Year</Btn>
    </Modal>
  </div>);
}

// ── App Router ──

export default function App() {
  const { path, navigate } = useRouter();

  let page;
  if (path === "/" || path === "") page = <HomePage navigate={navigate} />;
  else if (path === "/about") page = <AboutPage />;
  else if (path === "/contact") page = <ContactPage />;
  else if (path === "/privacy") page = <TextPage title="Privacy Policy" sections={privacySections} />;
  else if (path === "/terms") page = <TextPage title="Terms & Conditions" sections={termsSections} />;
  else if (path === "/events") page = <EventsPage navigate={navigate} />;
  else if (path.match(/^\/events\/(\d+)$/)) page = <EventYearPage yearId={path.match(/^\/events\/(\d+)$/)[1]} navigate={navigate} />;
  else if (path.match(/^\/events\/(\d+)\/horses$/)) page = <HorsesPage yearId={path.match(/^\/events\/(\d+)\/horses$/)[1]} navigate={navigate} />;
  else if (path.match(/^\/events\/(\d+)\/heats$/)) page = <HeatsPage yearId={path.match(/^\/events\/(\d+)\/heats$/)[1]} navigate={navigate} />;
  else if (path.match(/^\/events\/(\d+)\/gallery$/)) page = <GalleryPage yearId={path.match(/^\/events\/(\d+)\/gallery$/)[1]} navigate={navigate} />;
  else if (path.match(/^\/horse\/(\d+)$/)) page = <HorseProfilePage horseId={path.match(/^\/horse\/(\d+)$/)[1]} navigate={navigate} />;
  else if (path === "/login") page = <LoginPage navigate={navigate} />;
  else if (path === "/admin") page = <AdminDashboard navigate={navigate} />;
  else page = <PageShell title="404" subtitle="Page not found"><Btn onClick={() => navigate("/")}>Go Home</Btn></PageShell>;

  const isAdmin = path === "/admin";

  return (
    <AuthProvider>
      <div style={{ minHeight: "100vh", background: "#060c10", color: "#d0d8d0", fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@400;500;600;700&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @keyframes spin { to { transform: rotate(360deg); } }
          ::-webkit-scrollbar { width: 8px; }
          ::-webkit-scrollbar-track { background: #0a1628; }
          ::-webkit-scrollbar-thumb { background: rgba(200,164,90,0.3); border-radius: 4px; }
          @media (max-width: 768px) {
            .nav-desktop { display: none !important; }
            .nav-mobile-btn { display: block !important; }
          }
        `}</style>
        {!isAdmin && <Navbar navigate={navigate} path={path} />}
        {page}
        {!isAdmin && <Footer navigate={navigate} />}
      </div>
    </AuthProvider>
  );
}
